// Pure geographic helpers — no data, no imports. Data is sourced from the
// backend (`/api/cruises/current` + `/api/ckb/ports/*`) per ADR-006.

export function haversineNm([lat1, lon1], [lat2, lon2]) {
  const R = 3440; // nautical miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function bearingDeg([lat1, lon1], [lat2, lon2]) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const λ1 = toRad(lon1);
  const λ2 = toRad(lon2);
  const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Interpolate a position `t` (0..1) along an array of [lat, lon]
 * waypoints. Returns { lat, lon, heading }.
 */
export function interpolateAlong(waypoints, t) {
  if (!waypoints || waypoints.length < 2) {
    const p = waypoints?.[0] ?? [0, 0];
    return { lat: p[0], lon: p[1], heading: 0 };
  }
  const clamped = Math.max(0, Math.min(1, t));
  const legs = [];
  let total = 0;
  for (let i = 1; i < waypoints.length; i++) {
    const d = haversineNm(waypoints[i - 1], waypoints[i]);
    legs.push(d);
    total += d;
  }
  let target = clamped * total;
  for (let i = 0; i < legs.length; i++) {
    if (target <= legs[i] || i === legs.length - 1) {
      const local = legs[i] === 0 ? 0 : target / legs[i];
      const a = waypoints[i];
      const b = waypoints[i + 1];
      const lat = a[0] + (b[0] - a[0]) * local;
      const lon = a[1] + (b[1] - a[1]) * local;
      return { lat, lon, heading: bearingDeg(a, b) };
    }
    target -= legs[i];
  }
  const last = waypoints[waypoints.length - 1];
  return { lat: last[0], lon: last[1], heading: 0 };
}
